import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  RANGE_PRESETS,
  isMonthKey,
  monthLabel,
  monthRangeToken,
  parseMonthToken,
  parseRangeParam,
} from "./calendarMonth.mjs";
import { divisionColumnFor, isDivisionUnaware } from "./divisionSources.mjs";
import { resolveGrainAndRange } from "./grainDefaults.mjs";
import { humanize } from "./labels";
import { lobScopeNote, resolveLobFilter } from "./lobFilter.mjs";
import type { ManifestWidget } from "./manifest";

/**
 * Top-of-tab global filter controls. Two selectors that override each
 * widget's manifest-declared time slice:
 *
 * - Period type (monthly | weekly | daily) — overrides widget.filter.period_type
 *   when the manifest declares one.
 * - Range (mtd | wtd | l30 | l90 | ytd | this_month) — overrides widget's
 *   timeline.range for "current period" widgets so the user can flip
 *   between MTD and WTD without editing the manifest.
 * - Month (MER-692): the same `range` URL slot holding a calendar month,
 *   `?range=YYYY-MM`. It maps to the manifest token `month:YYYY-MM` and
 *   overrides exactly the widgets a preset overrides. A preset and a month
 *   are mutually exclusive by construction: one URL param holds either.
 *
 * State lives in URL search params so the view is shareable. Only
 * widgets whose timeline.mode is "period" and whose declared range is a
 * "current" period (this_month / this_week / last_month / last_week) get
 * the override; trailing-window widgets (last_4_months trend charts etc.)
 * keep their declared semantics.
 *
 * Manifest opt-out: set widget.respect_global_filters: false to skip
 * the override entirely (e.g. always-MTD balance sheet snapshots).
 */

export type GlobalPeriod = "monthly" | "weekly" | "daily";
export type GlobalRange = "mtd" | "wtd" | "l30" | "l90" | "ytd";

// MER-574: "daily" is offered but the pipeline only ever computes monthly and
// weekly KPI rows (pipeline.py calls compute_kpis with those two period types
// only). Selecting Daily writes period_type='daily' onto 83 of 197 DWS widgets,
// every one of which then matches zero rows and renders empty. The type keeps
// "daily" so a future daily grain needs no type change; the SELECTABLE list does
// not offer it until those rows exist.
const PERIOD_VALUES: readonly GlobalPeriod[] = ["monthly", "weekly"];
// MER-692: the preset list lives in calendarMonth.mjs so parseRangeParam and
// this selector cannot disagree about which strings are presets.
const RANGE_VALUES: readonly GlobalRange[] = RANGE_PRESETS;

// MER-611: this one IS a default and is applied. A widget that declares no
// `filter.period_type` on a period-aware source receives this value (the
// MER-362 auto-apply in grainDefaults.mjs), so "monthly" really is the grain
// those tiles answer at on a page nobody has touched. A widget that DECLARES
// a grain keeps it (MER-497).
const DEFAULT_PERIOD: GlobalPeriod = "monthly";
// MER-611: NOT a default, and the name that said it was misled a room of
// people with the code open (Delta review 2026-09-02; the meeting notes
// recorded "default range: year-to-date"). Nothing applies this value on a
// page with no `range` param: MER-421 removed the silent ytd promotion, so
// with Range on Default every widget keeps its AUTHORED timeline.range, which
// on the Overview disposal tiles is last_30_days and nowhere near ytd.
//
// What it is for: `range` is typed GlobalRange, not GlobalRange | undefined,
// so the hook needs a value to carry while no preset is selected.
// `rangeExplicit` is the gate on whether that value ever reaches a query, and
// the selector highlights off `rangeExplicit` rather than off this constant.
// Every consumer must read the pair, never this alone.
//
// Historical note, kept because it explains the constant's value rather than
// its meaning: MER-304 chose ytd over Last 30 days back when the value WAS
// promoted onto silent widgets, to stop sparse disposal columns rendering n/a.
// MER-421 ended the promotion; the value is inert and stays only so a future
// re-introduction does not silently pick a different window.
const RANGE_WHEN_UNSET: GlobalRange = "ytd";

// Map global range -> manifest range that the renderer already understands.
const RANGE_TO_MANIFEST: Record<GlobalRange, string> = {
  mtd: "this_month",
  wtd: "this_week",
  l30: "last_30_days",
  l90: "last_90_days",
  ytd: "ytd",
};

const RANGE_LABELS: Record<GlobalRange, string> = {
  mtd: "MTD",
  wtd: "WTD",
  // MER-584: short forms, per the approved mockup. "Last 30 days" plus the
  // four group labels pushed the control bar onto two rows; L30 / L90 keep it
  // on one. The manifest-facing labels (MANIFEST_RANGE_LABELS) stay verbose --
  // those read as prose on a tile face, not as a control.
  l30: "L30",
  l90: "L90",
  ytd: "YTD",
};

const PERIOD_LABELS: Record<GlobalPeriod, string> = {
  monthly: "Monthly",
  weekly: "Weekly",
  daily: "Daily",
};

/**
 * MER-421 Root 2: human label for a widget's EFFECTIVE (post-applyGlobalFilters)
 * timeline.range, so the on-tile window label always matches the data window --
 * the Default "last complete month", or whatever range the user selected. Ranges
 * not listed (trailing-window trends like last_4_months) return null and keep
 * their own naming.
 */
const MANIFEST_RANGE_LABELS: Record<string, string> = {
  this_month: "This month",
  last_month: "Last complete month",
  this_week: "This week",
  last_week: "Last week",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
  ytd: "YTD",
};

/**
 * MER-692: what a tile's rows are under an explicit month, by effective grain.
 * Monthly rows (and event-grain tiles, which carry no period_type) are the
 * calendar month itself and read as the bare month. Weekly and daily rows are
 * the weeks or days of that month, and the label says so: a weekly tile
 * covers the weeks the month mostly covers, which is not the calendar month.
 */
const MONTH_GRAIN_PREFIX: Record<string, string> = {
  weekly: "Weeks of ",
  daily: "Days of ",
};

/**
 * @param periodType the widget's EFFECTIVE grain (post-applyGlobalFilters
 *   filter.period_type). Only consulted for a month token; every other range
 *   label is grain-independent, exactly as before.
 */
export function manifestRangeLabel(
  range: string | undefined | null,
  periodType?: string | null,
): string | null {
  if (!range) return null;
  // MER-692: an explicit month names itself ("Jul 2026"), so an overridden
  // tile never keeps a stale "Last 30 days" under a calendar-month window.
  const month = parseMonthToken(range);
  if (month) {
    const label = monthLabel(month);
    if (label === null) return null;
    return `${(periodType && MONTH_GRAIN_PREFIX[periodType]) || ""}${label}`;
  }
  return MANIFEST_RANGE_LABELS[range] ?? null;
}

/** The grain a widget's filter carries: a bare string or an `{ op, value }` clause. */
function periodTypeOf(widget: ManifestWidget): string | undefined {
  const raw = (widget.filter as Record<string, unknown> | undefined)?.period_type;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object" && "value" in raw) {
    const value = (raw as { value?: unknown }).value;
    if (typeof value === "string") return value;
  }
  return undefined;
}

/**
 * MER-421 Root 2 + MER-692: the window label on a tile's face, read off the
 * widget AFTER applyGlobalFilters, which is the only place both the effective
 * range and the effective grain are known. Period-mode widgets only; a range
 * with no label (trailing trends) or a non-period widget gives null.
 */
export function widgetRangeLabel(widget: ManifestWidget): string | null {
  if (widget.timeline?.mode !== "period") return null;
  return manifestRangeLabel(widget.timeline.range, periodTypeOf(widget));
}

export interface GlobalFilters {
  period: GlobalPeriod;
  /** MER-497: true only when the URL carries an explicit `period` param
   * (setPeriod deletes the param at the default, so presence = a real user
   * selection). When false, applyGlobalFilters honors each widget's
   * DECLARED filter.period_type instead of clobbering it with the default. */
  periodExplicit: boolean;
  range: GlobalRange;
  /** MER-421 Root 2: true only when the URL carries an explicit PRESET `range`
   * param. When false (and `month` is null), applyGlobalFilters honors each
   * widget's declared range. */
  rangeExplicit: boolean;
  /** MER-692: the explicit calendar month the URL's `range` param holds
   * ('YYYY-MM'), or null. Mutually exclusive with `rangeExplicit`: the two
   * share one param. A month is an explicit range and overrides the same
   * widgets a preset does; with Period on Default it also rolls them up to
   * monthly grain (grainDefaults.mjs, the MER-692 exception). */
  month: string | null;
  /** MER-692: select a calendar month. Replaces any preset (one URL param)
   * and leaves Period alone, so Range Default afterwards returns every tile
   * to its declared default. The "Workbook month" chip uses it too. */
  setMonth: (month: string) => void;
  /** MER-163: multi-select division filter. Empty array = no filter
   * applied (matches all divisions). Each value is a manifest division
   * id (e.g. "jackson"). Persisted as `?divisions=jackson,hattiesburg`. */
  divisions: string[];
  /** MER-163: multi-select LOB / service-type filter. Empty array =
   * no filter applied. Each value is an enabled_services id (e.g.
   * "front_load"). Persisted as `?services=front_load,roll_off`. */
  services: string[];
  setPeriod: (p: GlobalPeriod) => void;
  /** MER-497: clear the period override, returning every widget to its
   * declared grain (the "Default" selector state). Mirror of clearRange. */
  clearPeriod: () => void;
  setRange: (r: GlobalRange) => void;
  /** MER-421 Root 2: clear the range override, returning every widget to its
   * manifest-declared range (the "Default" selector state). */
  clearRange: () => void;
  setDivisions: (d: string[]) => void;
  setServices: (s: string[]) => void;
}

function parseList(param: string | null): string[] {
  if (!param) return [];
  return param
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

export function useGlobalFilters(): GlobalFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  // MER-497: mirror the rangeExplicit pattern for the Period selector. A
  // period override only applies when the URL explicitly carries a valid
  // `period` param; setPeriod always writes the param (any click is an
  // explicit selection) and clearPeriod removes it (the "Default" chip).
  // `period` still resolves to DEFAULT_PERIOD for selector display and
  // for the MER-362 auto-apply on silent manifests.
  const periodRaw = searchParams.get("period");
  const periodExplicit =
    periodRaw !== null && (PERIOD_VALUES as readonly string[]).includes(periodRaw);
  const period = periodExplicit ? (periodRaw as GlobalPeriod) : DEFAULT_PERIOD;

  // MER-421 Root 2: a range override only applies when the URL explicitly carries
  // a valid `range` param. With no param, each widget keeps its manifest-declared
  // range (no silent ytd auto-promotion). `range` still resolves to
  // RANGE_WHEN_UNSET so the field has a value, but `rangeExplicit` gates whether
  // applyGlobalFilters rewrites a widget's timeline.range, and MER-611 renamed
  // the constant because its old name claimed a page default that does not
  // exist.
  //
  // MER-692: the same param may hold a calendar month ('YYYY-MM') instead of a
  // preset. parseRangeParam decides which; anything else (a typo, a bare year,
  // month 13) is treated exactly as a missing param, as before. A month leaves
  // rangeExplicit false (no preset chip lights) and sets `month` instead.
  const rangeParam = parseRangeParam(searchParams.get("range"));
  const rangeExplicit = rangeParam?.kind === "preset";
  const range: GlobalRange = rangeParam?.kind === "preset" ? rangeParam.range : RANGE_WHEN_UNSET;
  const month = rangeParam?.kind === "month" ? rangeParam.month : null;

  const divisions = parseList(searchParams.get("divisions"));
  const services = parseList(searchParams.get("services"));

  const setPeriod = useCallback(
    (next: GlobalPeriod) => {
      const params = new URLSearchParams(searchParams);
      // MER-497: always write the param so the selection is EXPLICIT and
      // overrides declared grains (the MER-421 setRange lesson: deleting
      // the param at the default made the default value un-selectable as
      // an explicit override). Returning to declared grains is clearPeriod().
      params.set("period", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearPeriod = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("period");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const setRange = useCallback(
    (next: GlobalRange) => {
      const params = new URLSearchParams(searchParams);
      // MER-421 Root 2: always write the param so the selection is EXPLICIT and the
      // override fires. Previously `next === RANGE_WHEN_UNSET` deleted the param, which
      // both made YTD un-selectable as an explicit override and re-enabled the
      // silent ytd auto-promotion. Returning to per-widget ranges is clearRange().
      params.set("range", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // MER-692: one param, so this clears a preset or a month alike.
  const clearRange = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("range");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const setMonth = useCallback(
    (next: string) => {
      // MER-692: writing the month into `range` replaces any preset. Guarded so
      // a malformed value can never reach a shareable URL.
      if (!isMonthKey(next)) return;
      const params = new URLSearchParams(searchParams);
      params.set("range", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setDivisions = useCallback(
    (next: string[]) => {
      const params = new URLSearchParams(searchParams);
      if (next.length === 0) params.delete("divisions");
      else params.set("divisions", next.join(","));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setServices = useCallback(
    (next: string[]) => {
      const params = new URLSearchParams(searchParams);
      if (next.length === 0) params.delete("services");
      else params.set("services", next.join(","));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return useMemo(
    () => ({
      period,
      periodExplicit,
      range,
      rangeExplicit,
      month,
      divisions,
      services,
      setPeriod,
      clearPeriod,
      setRange,
      clearRange,
      setMonth,
      setDivisions,
      setServices,
    }),
    // divisions/services compared by value so we serialize for the
    // dep array. JSON.stringify is fine for sorted-on-demand string arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      period,
      periodExplicit,
      range,
      rangeExplicit,
      month,
      divisions.join(","),
      services.join(","),
      setPeriod,
      clearPeriod,
      setRange,
      clearRange,
      setMonth,
      setDivisions,
      setServices,
    ],
  );
}

export const GLOBAL_PERIOD_LABELS: Record<GlobalPeriod, string> = PERIOD_LABELS;
export const GLOBAL_RANGE_LABELS: Record<GlobalRange, string> = RANGE_LABELS;
export const GLOBAL_PERIOD_VALUES = PERIOD_VALUES;
export const GLOBAL_RANGE_VALUES = RANGE_VALUES;

/**
 * Manifest ranges that the global range selector is allowed to override.
 * These are the "user-controllable view" ranges. Any widget defaulting
 * to one of these gets its timeline rewritten when the user toggles
 * MTD / WTD / L30 / L90 / YTD. Other manifest shapes (snapshot, explicit
 * historical trends like last_12_months) keep their declared semantics.
 *
 * MER-295: last_30_days + last_90_days were missing here even though
 * MER-285 flipped 71 single_number widgets to last_30_days as their
 * post-fraction-cutover default. Without these entries, the URL state
 * updated on selector click but the manifest range stayed at
 * last_30_days and the widget queries ignored the user selection. The
 * selector appeared decorative on /dws/Revenue. Adding both ranges so
 * the post-MER-285 defaults respect user override the same way the
 * pre-MER-285 calendar-period defaults did.
 */
const CURRENT_PERIOD_RANGES = new Set([
  "this_month",
  "this_week",
  "last_month",
  "last_week",
  "last_30_days",
  "last_90_days",
]);

/**
 * Source tables (Zoho display names) that carry a real `period_type`
 * column. Mirror of the canonical schema in
 * src/meridian_pipeline/supabase/schema.py: KPI_SUMMARY,
 * ROUTE_KPIS, and FINANCIAL_HEALTH all use (..., period_type,
 * period_start) as part of their primary key.
 *
 * MER-362: the global Period selector (Monthly / Weekly / Daily)
 * binds onto this column. Widgets with `timeline.mode: period`
 * sourced from one of these tables auto-receive
 * `filter.period_type` from the global selector even when their
 * manifest does not declare one. Widgets on event-grain sources
 * (service_event, ar_transactions, ...) have no period_type column;
 * for those, the Period selector is a no-op by source nature and
 * `timeline.range` is the user-facing knob.
 *
 * When the dashboard team adds a `period_type` axis to another
 * source table, add its display name here so the global selector
 * starts driving it without a per-widget manifest edit.
 */
const PERIOD_TYPE_AWARE_SOURCES = new Set([
  "Meridian - KPI Summary",
  "Meridian - Route KPIs",
  "Meridian - Financial Health",
]);

/**
 * Source tables that carry a real `equipment_type` column, i.e. the only
 * sources the page-level LOB (services) selector can drive. MER-423: the
 * global chip used to inject `equipment_type` onto every widget, assuming
 * PostgREST silently ignores a filter on a missing column. It does NOT --
 * it errors ("column financial_health.equipment_type does not exist"), so
 * Financial Health / AR* / Invoices (account-level, or `line_of_business`)
 * threw "WIDGET DATA UNAVAILABLE" under any LOB filter. Only inject for
 * these. Keep in sync with EQUIPMENT_TYPE_AWARE_SOURCES in
 * dashboard/scripts/audit-filters.mjs.
 */
// MER-504: table-kind sources whose tables carry NO division column
// (verified against migrations 0002/0010/0011: every canonical table
// has division except route and audit_finding; only the latter is a
// mapped widget source). Widgets on these say "Company-wide" under an
// active division filter instead of 42703ing.
// MER-582: division-column knowledge is shared with the audit harness in
// divisionSources.mjs so the two cannot drift apart again.

const EQUIPMENT_TYPE_AWARE_SOURCES = new Set([
  "Meridian - KPI Summary",
  "Meridian - Service Events",
  "Meridian - Route KPIs",
]);

interface WidgetWithOptIn extends ManifestWidget {
  respect_global_filters?: boolean;
  suppress_dashboard_filter?: boolean;
  /** MER-504: a suppressed widget that still wants ONLY the division
   * filter (e.g. AR Outstanding: financial_health has division grain,
   * but the widget must keep its snapshot period semantics). */
  division_scope?: string;
}

/**
 * MER-512: face label for a tile whose source has no `equipment_type`
 * column (Financial Health, AR*, Invoices) while an LOB selection is
 * active. MER-423 correctly stopped injecting the filter there (PostgREST
 * 42703s on the missing column), but that made the no-op SILENT -- the
 * tile looked frozen/broken. Surfacing it on the face stops it reading as
 * a bug.
 */
const LOB_NOT_FILTERED_NOTE = "Not filtered by LOB";

/** Append a scope note without clobbering one a prior dimension already
 * set (e.g. division's "Company-wide"); dedupe so re-entry is idempotent. */
function appendScopeNote(existing: string | undefined, note: string): string {
  if (!existing) return note;
  if (existing.split(" · ").includes(note)) return existing;
  return `${existing} · ${note}`;
}

/**
 * Return a copy of widget with global-filter overrides baked into
 * timeline.range and filter.period_type. The returned widget plugs into
 * the existing renderer code paths unchanged.
 *
 * Opt-out: `suppress_dashboard_filter: true` (canonical, documented in
 * the manifest schema header as "snapshot widgets MUST be true"), or
 * `respect_global_filters: false` (legacy alias). Either skips both
 * overrides. MER-155: AR Outstanding (L1.1b.3) declares
 * `suppress_dashboard_filter: true` and `filter.period_type: snapshot`,
 * but pre-fix the global override clobbered period_type back to the
 * default `monthly`, masking the snapshot rows and rendering n/a.
 */
export function applyGlobalFilters(
  widget: ManifestWidget,
  filters: {
    period: GlobalPeriod;
    periodExplicit?: boolean;
    range: GlobalRange;
    rangeExplicit?: boolean;
    /** MER-692: an explicit calendar month ('YYYY-MM'). When set it is the
     * explicit range, whatever `range` / `rangeExplicit` say. */
    month?: string | null;
    divisions?: string[];
    services?: string[];
    /** MER-694: the manifest's enabled_services, i.e. the lines the LOB chip
     * offers and the lines an unselected page shows. Only used to label a
     * scoped tile ("Roll Off only"); it never changes a filter. */
    enabledServices?: string[];
  },
): ManifestWidget {
  const w = widget as WidgetWithOptIn;
  if (w.suppress_dashboard_filter === true || w.respect_global_filters === false) {
    let optedOut: ManifestWidget = widget;
    // MER-504: an opted-out widget under an ACTIVE division filter must
    // never silently render company-wide numbers as if they were
    // divisional. Either it asked for division scoping (AR Outstanding)
    // or its face says "Company-wide".
    if (filters.divisions && filters.divisions.length > 0) {
      if (w.division_scope === "filter") {
        const existing = widget.filter ?? {};
        if (!("division" in existing)) {
          optedOut = {
            ...widget,
            filter: { ...existing, division: { op: "in", value: filters.divisions } },
          };
        }
      } else {
        optedOut = { ...widget, scope_note: "Company-wide" };
      }
    }
    // MER-616: the same rule for the LOB chip, which this path never applied.
    // Chris, 2026-09-02, watching a tile not move: "I see it go to roll off ...
    // it stayed identical." An opted-out widget is opted out of BOTH global
    // filters, so under an active selection it is showing every line while the
    // chip says one. Division has said so since MER-504 and LOB said nothing,
    // which left 44 DWS tiles (41 of them the AR / Financial Health / contract
    // family on the Overview, Billing and Sales tabs) indistinguishable from a
    // filter that had broken. The wording is the one MER-512 already uses for
    // the by-design no-op on the live path, so the two paths read alike.
    if (filters.services && filters.services.length > 0) {
      optedOut = {
        ...optedOut,
        scope_note: appendScopeNote(optedOut.scope_note, LOB_NOT_FILTERED_NOTE),
      };
    }
    return optedOut;
  }

  let next = widget;

  // MER-497: one pure decision for the widget's effective grain and its
  // default current-period window (see grainDefaults.mjs for the full
  // rationale and the MER-469 weekly-window dependency).
  //
  // Grain: a DECLARED filter.period_type survives the default load and is
  // only overridden by an explicit Period selection (URL carries `period`).
  // Silent manifests keep the MER-362 auto-apply: period-aware sources
  // (KPI Summary, Route KPIs, Financial Health) in period mode receive the
  // global value; event-grain sources (service_event, ar_transactions)
  // stay untouched, where adding the filter would 404 the PostgREST
  // request with `column does not exist`.
  //
  // Window (MER-421 Root 2, now keyed off the EFFECTIVE grain): an explicit
  // user `range` wins for every overridable widget. The DEFAULT resolves
  // monthly tiles to the last COMPLETE calendar month -- never the
  // in-progress partial month (e.g. Disposal Burden 179.7% on partial June
  // vs ~29% for the last complete month) -- and weekly tiles to a trailing
  // 30 days, the most recent complete weeks that pass the MER-469
  // now-cutoff. Trailing-window / trend widgets (not in
  // CURRENT_PERIOD_RANGES) and silent event-grain calendar windows keep
  // their declared semantics.
  // MER-582: resolve the source name for CQT views too. This was
  // table-kind-only, so every CQT widget got `sourceName === undefined`, sailed
  // past the division guard below, and was ALWAYS injected with a division
  // predicate -- including view_cqt_14 / 15 / 16, which project no division
  // column at all.
  const sourceName = widget.source ? widget.source.name : undefined;
  const widgetFilter = widget.filter ?? {};
  const declaresPeriodType = "period_type" in widgetFilter;
  const isPeriodMode = widget.timeline?.mode === "period";
  const sourceIsPeriodAware = sourceName !== undefined && PERIOD_TYPE_AWARE_SOURCES.has(sourceName);

  // MER-692: an explicit calendar month is an explicit range. It rides the
  // exact slot a preset does, so it overrides the same widgets (period mode,
  // declared range in CURRENT_PERIOD_RANGES: kpi_summary tiles and the
  // event-grain Overview service_event tiles alike) and leaves the same ones
  // alone (trailing trend charts, snapshots, suppressed widgets).
  const monthToken = monthRangeToken(filters.month);

  const { periodType: effectivePeriodType, range: effectiveRange } = resolveGrainAndRange({
    declaredPeriodType: declaresPeriodType ? (widgetFilter.period_type as string) : undefined,
    timelineIsPeriod: isPeriodMode,
    sourceIsPeriodAware,
    globalPeriod: filters.period,
    periodExplicit: filters.periodExplicit === true,
    declaredRange: widget.timeline?.range,
    declaredRangeIsCurrentPeriod: Boolean(
      widget.timeline &&
      isPeriodMode &&
      widget.timeline.range &&
      CURRENT_PERIOD_RANGES.has(widget.timeline.range),
    ),
    rangeExplicit: filters.rangeExplicit === true || monthToken !== null,
    globalManifestRange: monthToken ?? RANGE_TO_MANIFEST[filters.range],
  });

  if (effectiveRange !== undefined && widget.timeline) {
    next = {
      ...next,
      timeline: { ...widget.timeline, range: effectiveRange },
    };
  }

  if (effectivePeriodType !== undefined) {
    next = {
      ...next,
      filter: { ...widgetFilter, period_type: effectivePeriodType },
    };
  }

  // MER-163: divisions + services apply as IN filters, set only when the
  // widget does not already declare its own (the spread would otherwise
  // clobber a per-pane pin, e.g. KpiComparisonTile). We never overwrite an
  // existing filter at the same key.
  if (filters.divisions && filters.divisions.length > 0) {
    // MER-504: mirror the MER-423 equipment guard. A table-kind source
    // whose table has no division column must not receive the filter
    // (PostgREST 42703s, rendering the awaiting state over real data);
    // it says "Company-wide" on its face instead. Today only
    // audit_finding lacks the column; CQT sources keep the existing
    // inject-and-let-the-view-decide behavior.
    const sourceIsDivisionUnaware = isDivisionUnaware(sourceName);
    const existing = next.filter ?? {};
    if (sourceIsDivisionUnaware) {
      // MER-512: compose via appendScopeNote (not raw assignment) so this
      // note and the LOB block's "Not filtered by LOB" coexist on a source
      // that is both division- and equipment-unaware, order-independently.
      next = { ...next, scope_note: appendScopeNote(next.scope_note, "Company-wide") };
    } else if (!(divisionColumnFor(sourceName) in existing)) {
      // MER-582: inject under the column the SOURCE actually stores division
      // in. AR Current / AR Transactions / Site Contracts use division_name.
      next = {
        ...next,
        filter: {
          ...existing,
          [divisionColumnFor(sourceName)]: { op: "in", value: filters.divisions },
        },
      };
    }
  }

  // MER-423: only drive the LOB filter on sources that actually carry an
  // `equipment_type` column. PostgREST errors (not silently skips) on a
  // filter against a missing column, so account-level sources (Financial
  // Health, AR*, Invoices) cannot be LOB-filtered.
  const sourceIsEquipmentAware =
    sourceName !== undefined && EQUIPMENT_TYPE_AWARE_SOURCES.has(sourceName);
  if (filters.services && filters.services.length > 0) {
    if (!sourceIsEquipmentAware) {
      // MER-512 point 3: make the by-design no-op honest. The chip cannot
      // touch this source, so say so on the face instead of leaving it
      // looking like a broken filter.
      next = { ...next, scope_note: appendScopeNote(next.scope_note, LOB_NOT_FILTERED_NOTE) };
    } else {
      // MER-512 point 1: the never-overwrite rule (MER-163) froze every tile
      // that declared its own equipment_type -- which is almost all of them,
      // since the template pins a per-tenant rollup list (MER-422). Resolve
      // the effective clause: inject when unpinned, NARROW a multi-value
      // rollup list to the selection, leave a single-LOB pin (P&L card)
      // alone. See resolveLobFilter for the full decision table.
      // MER-629: a tile whose population is already one line of business by
      // other means (work_type literals, a single-line KPI column, a
      // customer's swap work types) declares `lob_scope` and is left alone
      // here, exactly as a scalar pin is. Injecting the chip on top of it
      // emptied Net FL Containers on every non-front-load selection.
      const existing = next.filter ?? {};
      const resolved = resolveLobFilter(
        existing.equipment_type,
        filters.services,
        widget.lob_scope,
      );
      // null = a scalar/eq pin OR a selection wholly outside a declared list
      // (empty intersection): keep the declared filter. Rollup tiles declare
      // the FULL enabled set, so for them it cannot fire. A scoped tile
      // (MER-694, e.g. the roll_off-only $/ton tiles) hits it whenever the
      // selection leaves its line out, and stays on its declared scope rather
      // than emitting an empty `in` that matches no rows.
      if (resolved !== null) {
        next = { ...next, filter: { ...existing, equipment_type: resolved } };
      }
    }
  }
  if (sourceIsEquipmentAware) {
    // MER-694: a scope that does not cover every line in view says which
    // lines it does show ("Roll Off only"), with or without a selection. On
    // the default load the lines in view are every enabled line, so the
    // Roll-Off-only Avg Cost / Ton on Overview is labeled next to the
    // all-line Total Tons and Total Disposal Cost instead of reading as their
    // quotient. Read off the DECLARED clause, before any narrowing above.
    const declaredLob = widget.filter?.equipment_type;
    const lobNote = lobScopeNote(
      declaredLob,
      filters.services ?? [],
      humanize,
      filters.enabledServices,
      widget.lob_scope,
    );
    if (lobNote !== null) {
      next = { ...next, scope_note: appendScopeNote(next.scope_note, lobNote) };
    }
  }

  return next;
}

/**
 * Exported for the MER-312 static audit harness so its classifier
 * stays in sync with the runtime auto-apply logic in
 * `applyGlobalFilters`. If you add a source here, the audit picks
 * it up the next time `npm run audit:filters` runs.
 */
export const GLOBAL_PERIOD_TYPE_AWARE_SOURCES = PERIOD_TYPE_AWARE_SOURCES;

/** MER-423: exported for the audit harness mirror (see PERIOD equivalent). */
export const GLOBAL_EQUIPMENT_TYPE_AWARE_SOURCES = EQUIPMENT_TYPE_AWARE_SOURCES;
